import { useState } from 'react';
import { ExternalLink, PenLine, Plus, ShieldCheck } from 'lucide-react';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { ZoneSignature } from '../signature/ZoneSignature';
import {
  useCreerDemandeSignature,
  useSignatures,
  useSigner,
  type DemandeSignature,
  type RoleSignataire,
  type SignataireAffiche,
} from '../../lib/useSignatures';
import { ErreurApi } from '../../lib/api';

/**
 * Signature des documents du dossier (CDC §5.4).
 *
 * Elle remplace l'écran « Signature électronique », qui listait les demandes de
 * toute une antenne. Le geste était pourtant toujours le même : on fait signer
 * **un document**, à propos d'**un dossier**, par des personnes que ce dossier
 * désigne — l'apprenti, son employeur, son représentant légal s'il est mineur.
 * Le traiter ici évite l'aller-retour entre le dossier, où le document est
 * produit, et une liste où il fallait le retrouver.
 *
 * Rien du circuit n'a changé : ce sont les mêmes `DemandeSignature` et
 * `Signataire`, la même route, le même scellement. C'est ce circuit que la
 * génération du CERFA alimente.
 *
 * L'écran annonce sans détour ce que vaut la signature recueillie : aucun
 * prestataire n'étant retenu, il s'agit d'un accusé interne horodaté.
 */

function tonStatut(demande: DemandeSignature): string {
  if (demande.statut === 'SIGNEE') return 'success';
  if (demande.statut === 'ANNULEE') return 'neutral';
  return demande.signesCount === 0 ? 'danger' : 'warning';
}

function horodatage(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function CarteSignatures({
  apprenantId,
  siteCode,
  /** Documents du dossier, proposés à la création d'une demande. */
  documents = [],
  /** Noms connus du dossier, pour ne pas ressaisir un signataire déjà là. */
  signatairesSuggeres = [],
  /**
   * Demander une signature est un geste de service. L'apprenant, lui, signe ce
   * qu'on lui présente : sa carte n'a pas le bouton, et l'API le refuserait de
   * toute façon.
   */
  peutDemander = true,
  titre = 'Signatures',
  /**
   * Lecture du document avant de signer : on ne signe pas un document sur son
   * seul titre. Fournie par l'espace apprenant, qui le sert par sa propre route.
   */
  lienDocument,
  /**
   * Lignes que ce compte peut signer. L'apprenant ne signe que la sienne —
   * l'API le refuse aussi — ; les autres attendent la tablette de l'équipe.
   */
  signataireAutorise = () => true,
}: {
  apprenantId?: string;
  siteCode: string;
  documents?: { id: string; libelle: string }[];
  signatairesSuggeres?: { role: RoleSignataire; nom: string }[];
  peutDemander?: boolean;
  titre?: string;
  lienDocument?: (documentId: string) => string;
  signataireAutorise?: (role: RoleSignataire) => boolean;
}): JSX.Element {
  const { data, isLoading, error } = useSignatures(siteCode, apprenantId);
  const creer = useCreerDemandeSignature();
  const signer = useSigner();

  const [creationOuverte, setCreationOuverte] = useState(false);
  const [aSigner, setASigner] = useState<{
    demande: DemandeSignature;
    signataire: SignataireAffiche;
  } | null>(null);

  const demandes = [...(data?.enAttente ?? []), ...(data?.signees ?? [])];

  if (isLoading) {
    return (
      <Carte titre={titre} sansBordure>
        <Chargement libelle="Chargement des demandes…" />
      </Carte>
    );
  }

  return (
    <Carte
      titre={titre}
      actions={
        peutDemander ? (
          <BoutonSecondaire onClick={() => setCreationOuverte(true)}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Faire signer un document
          </BoutonSecondaire>
        ) : undefined
      }
      sansBordure
    >
      <div className="space-y-4 p-4 sm:p-6">
        {error && (
          <MessageErreur
            message={
              error instanceof ErreurApi
                ? error.message
                : 'Les demandes de signature n’ont pas pu être chargées.'
            }
          />
        )}

        {data?.prestataire && !data.prestataire.operationnel && (
          <div className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-[11px] text-codialis-navy">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p>
              Aucun prestataire de signature électronique n’étant retenu, les signatures sont
              scellées en interne : tracé conservé, horodatage serveur et adresse IP. Elles ne
              valent pas signature qualifiée au sens du règlement eIDAS.
            </p>
          </div>
        )}

        {demandes.length === 0 ? (
          <p className="text-sm text-codialis-gray">
            {peutDemander
              ? 'Aucune demande de signature sur ce dossier. Le CERFA validé en crée une automatiquement ; les autres documents se font signer depuis le bouton ci-dessus.'
              : 'Aucun document ne vous attend pour signature.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {demandes.map((demande) => (
              <li key={demande.id} className="space-y-2 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-codialis-navy">
                      {demande.libelle}
                    </p>
                    <p className="text-[11px] text-codialis-gray">
                      Créée le {horodatage(demande.creeLe)}
                      {demande.scelleLe ? ` · scellée le ${horodatage(demande.scelleLe)}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {lienDocument && demande.documentId && (
                      <a
                        href={lienDocument(demande.documentId)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-h-[36px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                      >
                        Lire le document
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                        <span className="sr-only">(nouvel onglet)</span>
                      </a>
                    )}
                    <Pastille texte={demande.avancement} ton={tonStatut(demande)} />
                  </div>
                </div>

                <ul className="space-y-1">
                  {demande.signataires.map((signataire) => (
                    <li
                      key={signataire.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-sm text-codialis-navy">
                        {signataire.nom}{' '}
                        <span className="text-codialis-gray">({signataire.roleLibelle})</span>
                      </span>
                      {signataire.signeLe ? (
                        <span className="text-[11px] text-codialis-gray">
                          Signé le {horodatage(signataire.signeLe)}
                        </span>
                      ) : !signataireAutorise(signataire.role) ? (
                        <span className="text-[11px] text-codialis-gray">En attente</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setASigner({ demande, signataire })}
                          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-codialis-navy px-3 text-xs font-semibold text-white transition hover:bg-codialis-deep [@media(pointer:coarse)]:min-h-[40px]"
                        >
                          <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                          {peutDemander ? 'Faire signer' : 'Signer'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modale
        ouverte={creationOuverte}
        titre="Faire signer un document"
        onFermer={() => {
          creer.reset();
          setCreationOuverte(false);
        }}
      >
        <FormulaireDemande
          documents={documents}
          signatairesSuggeres={signatairesSuggeres}
          roles={data?.roles ?? []}
          enCours={creer.isPending}
          erreur={creer.error instanceof ErreurApi ? creer.error.message : null}
          onCreer={(entree) =>
            creer.mutate(
              { ...entree, apprenantId, site: siteCode },
              {
                onSuccess: () => {
                  creer.reset();
                  setCreationOuverte(false);
                },
              },
            )
          }
        />
      </Modale>

      <Modale
        ouverte={aSigner !== null}
        titre={aSigner ? `Signature · ${aSigner.demande.libelle}` : 'Signature'}
        onFermer={() => {
          signer.reset();
          setASigner(null);
        }}
      >
        {aSigner && lienDocument && aSigner.demande.documentId && (
          <a
            href={lienDocument(aSigner.demande.documentId)}
            target="_blank"
            rel="noreferrer"
            className="mb-3 inline-flex min-h-[40px] items-center gap-1.5 text-sm font-semibold text-codialis-sky hover:underline"
          >
            Lire le document avant de signer
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only">(nouvel onglet)</span>
          </a>
        )}
        {aSigner && (
          <ZoneSignature
            libelle={aSigner.demande.libelle}
            signataire={{
              nom: aSigner.signataire.nom,
              roleLibelle: aSigner.signataire.roleLibelle,
            }}
            empreinteDocument={aSigner.demande.empreinteDocument}
            enCours={signer.isPending}
            erreur={signer.error instanceof ErreurApi ? signer.error.message : null}
            resultat={
              signer.data
                ? { mention: signer.data.mention, scellee: signer.data.demande.statut === 'SIGNEE' }
                : null
            }
            onAnnuler={() => {
              signer.reset();
              setASigner(null);
            }}
            onSigner={(trace) =>
              signer.mutate(
                {
                  demandeId: aSigner.demande.id,
                  signataireId: aSigner.signataire.id,
                  trace,
                },
                {
                  onSuccess: () => {
                    signer.reset();
                    setASigner(null);
                  },
                },
              )
            }
          />
        )}
      </Modale>
    </Carte>
  );
}

const CLASSE_CHAMP =
  'w-full min-h-[44px] rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm';

/**
 * Création d'une demande.
 *
 * Les signataires sont **proposés** depuis le dossier — l'apprenti, son
 * représentant légal s'il est mineur, l'employeur — parce qu'ils y sont déjà
 * saisis et qu'une ressaisie est une occasion de se tromper de nom sur un
 * document contractuel. Ils restent corrigibles : c'est la personne qui signe
 * qui fait foi, pas la fiche.
 */
function FormulaireDemande({
  documents,
  signatairesSuggeres,
  roles,
  enCours,
  erreur,
  onCreer,
}: {
  documents: { id: string; libelle: string }[];
  signatairesSuggeres: { role: RoleSignataire; nom: string }[];
  roles: { valeur: RoleSignataire; libelle: string }[];
  enCours: boolean;
  erreur: string | null;
  onCreer: (entree: {
    libelle: string;
    documentId?: string;
    signataires: { role: RoleSignataire; nom: string }[];
  }) => void;
}): JSX.Element {
  const [documentId, setDocumentId] = useState('');
  const [libelle, setLibelle] = useState('');
  const [choisis, setChoisis] = useState<RoleSignataire[]>(
    signatairesSuggeres.map((suggere) => suggere.role),
  );
  const [ajoutes, setAjoutes] = useState<{ role: RoleSignataire; nom: string }[]>([]);

  const intitule =
    libelle.trim() || documents.find((document) => document.id === documentId)?.libelle || '';
  const signataires = [
    ...signatairesSuggeres.filter((suggere) => choisis.includes(suggere.role)),
    ...ajoutes.filter((ajout) => ajout.nom.trim().length >= 2),
  ];
  const complet = intitule.length >= 3 && signataires.length > 0;

  return (
    <form
      className="space-y-4 p-4 sm:p-6"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (!complet || enCours) return;
        onCreer({
          libelle: intitule,
          ...(documentId ? { documentId } : {}),
          signataires: signataires.map((signataire) => ({
            role: signataire.role,
            nom: signataire.nom.trim(),
          })),
        });
      }}
    >
      <div>
        <label
          htmlFor="signature-document"
          className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
        >
          Document du dossier
        </label>
        <select
          id="signature-document"
          value={documentId}
          onChange={(evenement) => setDocumentId(evenement.target.value)}
          className={CLASSE_CHAMP}
        >
          <option value="">Aucun — préciser l’intitulé ci-dessous</option>
          {documents.map((document) => (
            <option key={document.id} value={document.id}>
              {document.libelle}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="signature-libelle"
          className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
        >
          Intitulé {documentId ? '(facultatif)' : ''}
        </label>
        <input
          id="signature-libelle"
          value={libelle}
          onChange={(evenement) => setLibelle(evenement.target.value)}
          placeholder={
            documents.find((document) => document.id === documentId)?.libelle ??
            'Contrat d’apprentissage'
          }
          className={CLASSE_CHAMP}
        />
      </div>

      <fieldset>
        <legend className="mb-1 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          Signataires
        </legend>
        {signatairesSuggeres.length === 0 && ajoutes.length === 0 && (
          <p className="text-xs text-codialis-gray">
            Aucun signataire n’est déduit du dossier : ajoutez-en un ci-dessous.
          </p>
        )}
        <div className="space-y-2">
          {signatairesSuggeres.map((suggere) => (
            <label
              key={suggere.role}
              className="flex min-h-[44px] items-center gap-2 text-sm text-codialis-navy"
            >
              <input
                type="checkbox"
                className="h-5 w-5 rounded"
                checked={choisis.includes(suggere.role)}
                onChange={(evenement) =>
                  setChoisis((actuels) =>
                    evenement.target.checked
                      ? [...actuels, suggere.role]
                      : actuels.filter((role) => role !== suggere.role),
                  )
                }
              />
              {suggere.nom}{' '}
              <span className="text-codialis-gray">
                ({roles.find((role) => role.valeur === suggere.role)?.libelle ?? suggere.role})
              </span>
            </label>
          ))}

          {ajoutes.map((ajout, rang) => (
            <div key={rang} className="flex flex-col gap-2 sm:flex-row">
              <select
                value={ajout.role}
                aria-label="Rôle du signataire"
                onChange={(evenement) =>
                  setAjoutes((actuels) =>
                    actuels.map((entree, index) =>
                      index === rang
                        ? { ...entree, role: evenement.target.value as RoleSignataire }
                        : entree,
                    ),
                  )
                }
                className={`${CLASSE_CHAMP} sm:w-56`}
              >
                {roles.map((role) => (
                  <option key={role.valeur} value={role.valeur}>
                    {role.libelle}
                  </option>
                ))}
              </select>
              <input
                value={ajout.nom}
                aria-label="Nom du signataire"
                onChange={(evenement) =>
                  setAjoutes((actuels) =>
                    actuels.map((entree, index) =>
                      index === rang ? { ...entree, nom: evenement.target.value } : entree,
                    ),
                  )
                }
                placeholder="Nom et prénom"
                className={CLASSE_CHAMP}
              />
            </div>
          ))}
        </div>

        <BoutonSecondaire
          onClick={() =>
            setAjoutes((actuels) => [
              ...actuels,
              { role: roles[0]?.valeur ?? 'CFA', nom: '' },
            ])
          }
        >
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Ajouter un signataire
        </BoutonSecondaire>
      </fieldset>

      {erreur && <MessageErreur message={erreur} />}

      <button
        type="submit"
        disabled={!complet || enCours}
        className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
      >
        {enCours ? 'Création…' : 'Créer la demande'}
      </button>
    </form>
  );
}
